import { Flex } from "@radix-ui/themes";
import { Button, Skeleton, TextField, TextFieldSlot } from "../elements.js";
import { GenericError } from "../generic-error.js";
import { EmptyState } from "../empty-state.js";
import { MagnifyingGlassIcon } from "@radix-ui/react-icons";
import { SkeletonTable } from "./skeleton-table.js";
import { CreateApiKeyButton } from "./create-api-key.js";
import { getDomProps, WidgetRootDomProps, WidgetRootState } from "../utils.js";
import { ListOrganizationApiKeysResponse } from "../../api/endpoint.js";
import { ApiKeysSearchProvider } from "./api-keys-search-provider.js";
import { ApiKeysTable } from "./api-keys-table.js";
import { Translation } from "../i18n/translation.js";
import { useTranslation } from "../i18n/use-translation.js";

interface ApiKeysProps extends WidgetRootDomProps {
  apiKeys: ListOrganizationApiKeysResponse;
  searchQuery: string | null;
}

const ApiKeys: React.FC<ApiKeysProps> = ({
  apiKeys,
  searchQuery,
  ...domProps
}) => {
  return apiKeys.data.length === 0 && !searchQuery ? (
    <ApiKeysEmptyState {...getWidgetRootDomProps("resolved", domProps)} />
  ) : (
    <ApiKeysSearchProvider>
      <ApiKeysTable
        apiKeys={apiKeys}
        {...getWidgetRootDomProps("resolved", domProps)}
      />
    </ApiKeysSearchProvider>
  );
};

interface ApiKeysLoadingProps extends WidgetRootDomProps {}

const ApiKeysLoading: React.FC<ApiKeysLoadingProps> = (props) => {
  const translate = useTranslation();
  return (
    <Flex
      direction="column"
      align="stretch"
      gap="3"
      {...getWidgetRootDomProps("loading", props)}
    >
      <Flex justify="between" align="center">
        <Skeleton>
          <TextField
            name="api-keys-widget-search"
            style={{ width: "390px" }}
            placeholder={translate({
              defaultMessage: "Search by name",
              id: "VBESVz",
              description:
                "Placeholder for API key search input in loading state",
            })}
          >
            <TextFieldSlot side="left">
              <MagnifyingGlassIcon aria-hidden="true" height="16" width="16" />
            </TextFieldSlot>
          </TextField>
        </Skeleton>
        <Skeleton>
          <Button>
            <Translation
              defaultMessage="Create API key"
              id="eTEzjH"
              description="Button to create a new API key in loading state"
            />
          </Button>
        </Skeleton>
      </Flex>
      <SkeletonTable
        numRows={5}
        columns={[
          { children: "Name", width: "25%" },
          { children: "Secret key", width: "25%" },
          { children: "Last used", width: "25%" },
          { children: "Created", width: "25%" },
        ]}
      />
    </Flex>
  );
};

interface ApiKeysErrorProps extends WidgetRootDomProps {
  error: unknown;
}

const ApiKeysError: React.FC<ApiKeysErrorProps> = ({ error, ...domProps }) => {
  return (
    <GenericError error={error} {...getWidgetRootDomProps("error", domProps)} />
  );
};

function ApiKeysEmptyState(props: WidgetRootDomProps) {
  return (
    <EmptyState
      heading="No API keys yet"
      message="Generate an API key to authenticate and interact with our API."
      action={<CreateApiKeyButton />}
      {...props}
    />
  );
}

function getWidgetRootDomProps(
  state: WidgetRootState,
  domProps: WidgetRootDomProps,
) {
  return getDomProps({
    ...domProps,
    isWidgetRoot: true,
    widgetId: "api-keys",
    widgetState: state,
  });
}

export type { ApiKeysProps, ApiKeysLoadingProps, ApiKeysErrorProps };
export { ApiKeys, ApiKeysLoading, ApiKeysError };
